import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import { Box, FormControl, FormLabel, Typography, useMediaQuery } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairsFromChildProps, getVisibleError, handleWidgetUpdate, joinLabels, makeNewConfigUnique, } from '../WidgetForm/Utils';
import { BaseLayoutColumn, BaseLayoutRow } from './Components';
import { type } from './types';
const WidgetFormPanel = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    const responsive = (_b = (_a = props.config.style) === null || _a === void 0 ? void 0 : _a.layout) === null || _b === void 0 ? void 0 : _b.includes('responsive');
    const responsiveBreakpoint = (responsive ? (_d = (_c = props.config.style) === null || _c === void 0 ? void 0 : _c.layout) === null || _d === void 0 ? void 0 : _d.replace('responsive-', '') : 'xl');
    const matchesResponsiveBreakpoint = useMediaQuery((theme) => theme.breakpoints.down(responsiveBreakpoint));
    let layoutRow;
    if (responsive) {
        layoutRow = !matchesResponsiveBreakpoint;
    }
    else {
        layoutRow = ((_e = props.config.style) === null || _e === void 0 ? void 0 : _e.layout) === 'row';
    }
    const error = getVisibleError(props);
    const spacing = (_g = (_f = props.config.style) === null || _f === void 0 ? void 0 : _f.spacing) !== null && _g !== void 0 ? _g : 16;
    let children = layoutRow ? (React.createElement(BaseLayoutRow, { spacing: spacing, horizontalAlign: (_h = props.config.style) === null || _h === void 0 ? void 0 : _h.horizontalAlign, verticalAlign: (_j = props.config.style) === null || _j === void 0 ? void 0 : _j.verticalAlign, childrenEditorPath: 'children', childProps: (_k = props.childProps) !== null && _k !== void 0 ? _k : [], renderData: props.renderData, editMode: props.editMode })) : (React.createElement(BaseLayoutColumn, { spacing: spacing, horizontalAlign: (_l = props.config.style) === null || _l === void 0 ? void 0 : _l.horizontalAlign, childrenEditorPath: 'children', childProps: (_m = props.childProps) !== null && _m !== void 0 ? _m : [], renderData: props.renderData, editMode: props.editMode }));
    let labelVariant = undefined;
    if ((_o = props.config.style) === null || _o === void 0 ? void 0 : _o.surface) {
        switch ((_p = props.config.style) === null || _p === void 0 ? void 0 : _p.surface) {
            case 'level1':
                labelVariant = 'subtitle1';
                children = React.createElement(Box, { sx: { p: 2, backgroundColor: themeColors.surface.secondary } }, children);
                break;
            case 'level2':
                labelVariant = 'subtitle2';
                children = React.createElement(Box, { sx: { p: 2, backgroundColor: themeColors.surface.tertiary } }, children);
                break;
            case 'level3':
                labelVariant = 'body2';
                children = React.createElement(Box, { sx: { p: 2, backgroundColor: themeColors.surface.secondary } }, children);
                break;
            case 'level4':
                labelVariant = 'body2';
                children = React.createElement(Box, { sx: { p: 2, backgroundColor: themeColors.surface.tertiary } }, children);
                break;
            case 'level5':
                labelVariant = 'body2';
                children = React.createElement(Box, { sx: { backgroundColor: themeColors.surface.tertiary } }, children);
                break;
        }
    }
    if (props.editMode) {
        const needPaddingForEditor = !((_q = props.config.style) === null || _q === void 0 ? void 0 : _q.surface) || props.config.style.surface === 'level5';
        children = (React.createElement(Box, { sx: { p: needPaddingForEditor ? 2 : 0, outline: (theme) => '1px dashed ' + theme.palette.divider } }, children));
    }
    if (props.config.label || props.config.description || !!error) {
        return (React.createElement(FormControl, { fullWidth: true, error: !!error },
            props.config.label && (React.createElement(FormLabel, { sx: { mb: 2, typography: labelVariant }, error: !!error }, labelVariant ? React.createElement(Typography, { variant: labelVariant }, props.config.label) : props.config.label)),
            children,
            React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description })));
    }
    else {
        return React.createElement(Box, null, children);
    }
};
const WidgetFormPanelEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Panel: ' + props.config.label }, buildGeneralTab(props, { keyOptional: true, label: true, description: true }), buildHiddenTab(props), buildStyleTab(props, {
            onLoad: (data) => {
                if (!data.style) {
                    data.style = {};
                }
                if (!data.style.layout) {
                    data.style.layout = 'column';
                }
                if (!data.style.horizontalAlign) {
                    data.style.horizontalAlign = 'left';
                }
                if (!data.style.verticalAlign) {
                    data.style.verticalAlign = 'top';
                }
                return data;
            },
            onSubmit: (data) => {
                var _a, _b, _c, _d;
                if (((_a = data.style) === null || _a === void 0 ? void 0 : _a.layout) === 'column') {
                    (_b = data.style) === null || _b === void 0 ? true : delete _b.layout;
                    delete data.style.verticalAlign;
                }
                if (((_c = data.style) === null || _c === void 0 ? void 0 : _c.horizontalAlign) === 'stretch') {
                    delete data.style.horizontalAlign;
                }
                if (((_d = data.style) === null || _d === void 0 ? void 0 : _d.verticalAlign) === 'top') {
                    delete data.style.verticalAlign;
                }
                return data;
            },
            children: [
                {
                    type: 'form.number',
                    key: 'style.spacing',
                    label: 'Spacing',
                    validate: {
                        min: 0,
                    },
                    style: {
                        width: 200,
                    },
                },
                {
                    type: 'form.select',
                    key: 'style.layout',
                    label: 'Layout',
                    showDescription: true,
                    values: [
                        {
                            value: 'column',
                            label: 'Column',
                            description: 'Layout children in single column, from top to bottom',
                        },
                        { value: 'row', label: 'Row', description: 'Layout children in single row, from left to right' },
                        {
                            value: 'responsive-sm',
                            label: 'Responsive SM',
                            description: 'Column if screen width < 600px, otherwise row',
                        },
                        {
                            value: 'responsive-md',
                            label: 'Responsive MD',
                            description: 'Column if screen width < 900px, otherwise row',
                        },
                        {
                            value: 'responsive-lg',
                            label: 'Responsive LG',
                            description: 'Column if screen width < 1200px, otherwise row',
                        },
                        {
                            value: 'responsive-xl',
                            label: 'Responsive XL',
                            description: 'Column if screen width < 1536px, otherwise row',
                        },
                    ],
                },
                {
                    type: 'form.select',
                    key: 'style.surface',
                    label: 'Surface',
                    values: [
                        {
                            value: 'level1',
                            label: 'Level 1',
                        },
                        {
                            value: 'level2',
                            label: 'Level 2',
                        },
                        {
                            value: 'level3',
                            label: 'Level 3',
                        },
                        {
                            value: 'level4',
                            label: 'Level 4',
                        },
                        {
                            value: 'level5',
                            label: 'Level 5',
                        },
                    ],
                },
                {
                    type: 'form.radio',
                    key: 'style.horizontalAlign',
                    label: 'Horizontal Alignment',
                    values: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                    style: {
                        inline: true,
                    },
                },
                {
                    type: 'form.radio',
                    key: 'style.verticalAlign',
                    label: 'Vertical Alignment',
                    values: [
                        { value: 'top', label: 'Top' },
                        { value: 'center', label: 'Center' },
                        { value: 'bottom', label: 'Bottom' },
                    ],
                    hidden: {
                        when: '^style.layout',
                        dataEq: 'column',
                    },
                    style: {
                        inline: true,
                    },
                },
            ],
        }), buildEventTab(props, { eventOnChange: true })) }),
        React.createElement(WidgetFormPanel, Object.assign({}, props))));
};
export const widgetFormPanel = {
    type,
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        return getLabelAndValuePairsFromChildProps(props, joinLabels(labelPrefix, props.config.label));
    },
    childConfigs: (config) => config.children,
    component: WidgetFormPanel,
    icon: React.createElement(BorderAllIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormPanelEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, children: [] }),
};
